import React from 'react';
import { Chip, Button } from '@heroui/react';
import { Award, Paperclip, Pencil, Trash2 } from 'lucide-react';
import { expiryState } from '../documentDates.js';
import { formatUserDate } from '../formatUserDate.js';

/**
 * CertificatesTable — the certificate list.
 *
 * Same construction as `DocumentsTable`, and for the same two reasons: the
 * `role` attributes are load-bearing (`react-heroui-layout.twig` zeroes
 * padding on role-less cells), and `table-fixed` + a colgroup keeps long
 * values truncating inside their cell instead of widening the table into a
 * horizontal scroll the responsive contract forbids.
 */
function CertificatesTable({ certificates, typeName, gradeName, onView, onEdit, onDelete, busyId }) {
	if (!certificates.length) {
		return (
			<div className="bg-content1 border border-default-200 rounded-xl py-14 px-6 text-center">
				<p className="text-[15px] font-semibold text-foreground m-0">No certificates yet</p>
				<p className="text-sm text-default-500 mt-1 mb-0">
					Add a pilot certificate, rating or medical to keep it on file.
				</p>
			</div>
		);
	}

	return (
		<>
			{/* Phone: cards — the table's six columns cannot fit without a sideways scroll. */}
			{/* Hidden on a wrapper: the layout forces `.flex { display: flex !important }`,
			    which would beat `md:hidden` on the list itself. */}
			<div className="md:hidden">
				<ul className="list-none p-0 m-0 flex flex-col gap-2">
					{certificates.map(cert => {
						const exp = expiryState(cert.dateExpires);
						const scans = Array.isArray(cert.attachments) ? cert.attachments.length : 0;
						return (
							<li key={cert.id}>
								<button
									type="button"
									onClick={() => onView(cert)}
									className="w-full text-left bg-content1 border border-default-200 rounded-xl px-4 py-3 flex flex-col gap-1.5 active:bg-default-50"
								>
									<span className="flex items-start justify-between gap-2">
										<span className="text-[14px] font-medium text-foreground break-words">
											{typeName(cert.type) || 'Certificate'}
										</span>
										{exp ? (
											<Chip size="sm" variant="flat" color={exp.tone} className="shrink-0">
												{exp.label}
											</Chip>
										) : null}
									</span>
									<span className="text-[13px] text-default-500 break-words">
										{[gradeName(cert.grade), cert.number].filter(Boolean).join(' · ') || '—'}
									</span>
									<span className="flex items-center justify-between gap-2 text-[13px] text-default-500">
										<span>
											{cert.dateExpires
												? `Expires ${formatUserDate(cert.dateExpires)}`
												: cert.dateOfIssue
													? `Issued ${formatUserDate(cert.dateOfIssue)}`
													: 'No expiration'}
										</span>
										{scans > 0 ? (
											<span className="inline-flex items-center gap-1">
												<Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
												{scans}
											</span>
										) : null}
									</span>
								</button>
							</li>
						);
					})}
				</ul>
			</div>

			{/* `max-md:hidden`, not `hidden md:block` — Bootstrap's `.hidden` is !important. */}
			<div className="max-md:hidden bg-content1 border border-default-200 rounded-xl overflow-hidden">
				<table className="w-full border-collapse table-fixed">
					<colgroup>
						<col className="w-[26%]" />
						<col className="w-[19%]" />
						<col className="w-[16%]" />
						<col className="w-[14%]" />
						<col className="w-[14%]" />
						<col className="w-[11%]" />
					</colgroup>
					<thead>
						<tr className="bg-default-100">
							{['Certificate Type', 'Grade', 'Number', 'Issued', 'Expires', 'Actions'].map(h => (
								<th
									key={h}
									role="columnheader"
									className="text-left text-[12px] font-semibold tracking-wide text-default-500 border-b border-default-200"
								>
									{h}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{certificates.map(cert => {
							const exp = expiryState(cert.dateExpires);
							return (
								<tr
									key={cert.id}
									tabIndex={0}
									onClick={() => onView(cert)}
									onKeyDown={e => {
										if (e.key === 'Enter') onView(cert);
									}}
									className="border-b border-default-200 last:border-b-0 cursor-pointer hover:bg-default-50 focus-visible:outline-2 focus-visible:outline-primary"
								>
									<td role="cell" className="align-middle">
										<span className="flex items-center gap-2 min-w-0">
											<Award className="w-4 h-4 shrink-0 text-default-400" aria-hidden="true" />
											<span className="text-[14px] font-medium text-foreground truncate">
												{typeName(cert.type) || 'Certificate'}
											</span>
										</span>
									</td>
									<td role="cell" className="text-[14px] text-default-500 align-middle">
										<span className="block truncate">{gradeName(cert.grade) || '—'}</span>
									</td>
									<td role="cell" className="text-[14px] text-default-500 align-middle">
										<span className="block truncate">{cert.number || '—'}</span>
									</td>
									<td role="cell" className="text-[14px] text-default-500 align-middle">
										{formatUserDate(cert.dateOfIssue, '—')}
									</td>
									<td role="cell" className="align-middle">
										{exp ? (
											<span className="flex flex-wrap items-center gap-x-2 gap-y-1">
												<span className="text-[14px] text-default-500">
													{formatUserDate(cert.dateExpires)}
												</span>
												<Chip size="sm" variant="flat" color={exp.tone}>
													{exp.label}
												</Chip>
											</span>
										) : (
											<span className="text-[14px] text-default-400">No expiration</span>
										)}
									</td>
									{/* The row opens the preview; its buttons must not. */}
									<td
										role="cell"
										className="align-middle"
										onClick={e => e.stopPropagation()}
										onKeyDown={e => e.stopPropagation()}
									>
										<div className="flex items-center gap-1">
											<Button
												isIconOnly
												size="sm"
												variant="light"
												aria-label="Edit certificate"
												onPress={() => onEdit(cert)}
											>
												<Pencil className="w-4 h-4" />
											</Button>
											<Button
												isIconOnly
												size="sm"
												variant="light"
												color="danger"
												isLoading={busyId === cert.id}
												aria-label="Delete certificate"
												onPress={() => onDelete(cert)}
											>
												<Trash2 className="w-4 h-4" />
											</Button>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</>
	);
}

export default CertificatesTable;
